<template>
  <div class="commerce-seo-meta-fields">
    <template v-if="!embedded">
      <h3 class="section-title">{{ sectionHeading }}</h3>
      <FormTipBox v-if="introHtml" spacing="section">
        <p class="form-hint" v-html="introHtml" />
      </FormTipBox>
      <slot name="intro-extra" />
    </template>

    <div :class="fieldWrapperClass">
      <label class="form-label">SEO 標題</label>
      <input
        v-model="seoTitleModel"
        class="form-control"
        :placeholder="copy.titlePlaceholder"
      />
      <FormTipBox spacing="compact">
        <p :class="hintParagraphClass" v-html="copy.titlePurposeHtml" />
        <p v-if="copy.titleFallbackHtml" :class="hintParagraphClass" v-html="copy.titleFallbackHtml" />
        <p :class="exampleHintClass">
          <strong>設定範例：</strong><span class="seo-example-text">{{ copy.titleExample }}</span>
        </p>
      </FormTipBox>
    </div>

    <div :class="fieldWrapperClass">
      <label class="form-label">SEO 描述</label>
      <textarea
        v-model="seoDescriptionModel"
        class="form-control"
        :rows="descriptionRows"
        :placeholder="copy.descPlaceholder"
      />
      <FormTipBox spacing="compact">
        <p :class="hintParagraphClass" v-html="copy.descPurposeHtml" />
        <p v-if="copy.descFallbackHtml" :class="hintParagraphClass" v-html="copy.descFallbackHtml" />
        <p :class="exampleHintClass">
          <strong>設定範例：</strong><span class="seo-example-text">{{ copy.descExample }}</span>
        </p>
      </FormTipBox>
    </div>

    <div :class="fieldWrapperClass">
      <label class="form-label">SEO 關鍵字</label>
      <input
        v-model="seoKeywordsModel"
        class="form-control"
        placeholder="多個關鍵字請用英文逗號分隔"
      />
      <FormTipBox spacing="compact">
        <p :class="hintParagraphClass" v-html="copy.keywordsPurposeHtml" />
        <p :class="hintParagraphClass"><strong>填寫方式：</strong>{{ copy.keywordsHow }}</p>
        <p :class="exampleHintClass">
          <strong>設定範例：</strong><span class="seo-example-text">{{ copy.keywordsExample }}</span>
        </p>
      </FormTipBox>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import FormTipBox from '@/components/form/FormTipBox.vue'

const props = defineProps({
  seoTitle: { type: String, default: '' },
  seoDescription: { type: String, default: '' },
  seoKeywords: { type: String, default: '' },
  /** product：嵌入商品表單（無區塊標題）；hall、storefrontPage：完整 SEO 區塊 */
  variant: {
    type: String,
    required: true,
    validator: (v) => ['product', 'hall', 'storefrontPage'].includes(v),
  },
  /** true：只輸出三欄位（商品頁另含 URL Slug 等） */
  embedded: { type: Boolean, default: false },
})

const emit = defineEmits(['update:seoTitle', 'update:seoDescription', 'update:seoKeywords'])

const seoTitleModel = computed({
  get: () => props.seoTitle,
  set: (v) => emit('update:seoTitle', v ?? ''),
})

const seoDescriptionModel = computed({
  get: () => props.seoDescription,
  set: (v) => emit('update:seoDescription', v ?? ''),
})

const seoKeywordsModel = computed({
  get: () => props.seoKeywords,
  set: (v) => emit('update:seoKeywords', v ?? ''),
})

const fieldWrapperClass = computed(() =>
  props.variant === 'product' && props.embedded ? 'form-row form-row-block' : 'form-group'
)

const descriptionRows = computed(() => {
  if (props.variant === 'storefrontPage') return 2
  if (props.variant === 'product' && props.embedded) return 2
  return 3
})

const hintParagraphClass = computed(() =>
  props.variant === 'product' && props.embedded ? 'field-hint' : 'hint-p'
)

const exampleHintClass = computed(() =>
  props.variant === 'product' && props.embedded
    ? ['field-hint', 'field-hint-example']
    : ['hint-p', 'hint-p--example']
)

const COPY = {
  product: {
    sectionHeading: 'SEO 與前台設定',
    introHtml: '',
    titlePlaceholder: '覆寫商品名稱（選填）',
    titlePurposeHtml:
      '<strong>用途：</strong>對應網頁 <code>&lt;title&gt;</code>，會出現在<strong>搜尋結果標題</strong>與<strong>瀏覽器分頁</strong>。宜簡短、含商品或賣點與必要時品牌名；長度建議約 25～35 個中文字以內，避免遭搜尋引擎截斷。',
    titleFallbackHtml: '<strong>留空時：</strong>前台多半沿用「商品名稱」（基本資料區）。',
    titleExample: '輕量透氣慢跑鞋 男款｜鎖線選物官方商城',
    descPlaceholder: '搜尋引擎摘要說明（選填）',
    descPurposeHtml:
      '<strong>用途：</strong>對應 <code>meta name=\"description\"</code>，常作為搜尋結果下方的<strong>摘要</strong>或分享連結預覽。建議一句話說清賣點、規格亮點或適用對象；長度約 70～120 字為佳（實際顯示依平台略有差異）。',
    descFallbackHtml:
      '<strong>留空時：</strong>前台可能改用「商品簡述」或「內容簡介」摘錄（依前台實作），摘要不一定如您預期，重點商品建議手動填寫。',
    descExample: '網布透氣、緩震中底，適合日常慢跑與輕運動。多色可選，會員享專屬優惠。',
    keywordsPurposeHtml:
      '<strong>用途：</strong>寫入商品主檔的 <code>seo_keywords</code>，可供前台產生 <code>meta keywords</code>、結構化資料或站內搜尋加權（依前台實作而定）。主要搜尋引擎對 meta keywords 權重已很低，但仍可作為<strong>內部標記</strong>與部分通路／工具使用。',
    keywordsHow: '建議 5～10 個與商品最相關的詞，以英文逗號分隔；避免堆砌無關字詞。',
    keywordsExample: '慢跑鞋, 運動鞋, 透氣, 男鞋, 休閒鞋',
  },
  hall: {
    sectionHeading: 'SEO 設定',
    introHtml:
      '<strong>整體說明：</strong>這裡的內容主要給搜尋引擎與社群／通訊軟體預覽使用（例如 Google 搜尋結果標題與摘要、分享連結時的標題與描述）。若<strong>留空</strong>，前台通常會改用「館別名稱」與「描述」等既有欄位；若有重要活動字樣或想優化點閱率，建議在此填寫較精簡、吸引人的文案。',
    titlePlaceholder: '選填；留空則多半沿用館別名稱',
    titlePurposeHtml:
      '<strong>用途：</strong>對應網頁 <code>&lt;title&gt;</code>，會出現在<strong>搜尋結果標題</strong>與<strong>瀏覽器分頁</strong>。宜簡短、含活動或館別重點，必要時可帶品牌名；長度建議約 25～35 個中文字以內，避免遭搜尋引擎截斷。',
    titleFallbackHtml: null,
    titleExample: '春季特惠館｜鎖線選物官方商城',
    descPlaceholder: '選填；簡短說明本館特色或活動',
    descPurposeHtml:
      '<strong>用途：</strong>對應 <code>meta name=\"description\"</code>，常作為搜尋結果下方的<strong>摘要</strong>或分享預覽說明。建議一句話說清「這個館提供什麼」、為何必看，並可含行動呼籲；長度約 70～120 字為佳（實際顯示長度依平台略有差異）。',
    descFallbackHtml: null,
    descExample:
      '本館集結限時折扣與季節新品，涵蓋服飾與生活選物。逛館別精選商品，把握優惠檔期。',
    keywordsPurposeHtml:
      '<strong>用途：</strong>寫入館別主檔的 <code>seo_keywords</code>，可供前台產生 <code>meta keywords</code>、結構化資料或站內搜尋加權（依前台實作而定）。',
    keywordsHow: '建議與本館主題相關的詞，以英文逗號分隔。',
    keywordsExample: '周年慶, 特價館, 新品, 限時優惠',
  },
  storefrontPage: {
    sectionHeading: 'SEO 設定',
    introHtml:
      '<strong>整體說明：</strong>這裡的內容主要給搜尋引擎與社群／通訊軟體預覽使用（例如 Google 搜尋結果標題與摘要、分享連結時的標題與描述）。若<strong>留空</strong>，前台多半會改用「頁面標題」與頁面內容摘錄；若有品牌字樣或想優化點閱率，建議在此填寫較精簡、吸引人的文案。',
    titlePlaceholder: '覆寫頁面標題（選填）',
    titlePurposeHtml:
      '<strong>用途：</strong>對應網頁 <code>&lt;title&gt;</code>，會出現在<strong>搜尋結果標題</strong>與<strong>瀏覽器分頁</strong>。宜簡短、含頁面或品牌重點；長度建議約 25～35 個中文字以內，避免遭搜尋引擎截斷。',
    titleFallbackHtml: '<strong>留空時：</strong>前台通常沿用「頁面標題」（基本資訊區）。',
    titleExample: '關於我們｜鎖線選物官方商城',
    descPlaceholder: '搜尋引擎描述（選填）',
    descPurposeHtml:
      '<strong>用途：</strong>對應 <code>meta name=\"description\"</code>，常作為搜尋結果下方的<strong>摘要</strong>或分享預覽說明。建議一句話說清「這頁內容為何值得點入」，並可含行動呼籲；長度約 70～120 字為佳（實際顯示依平台略有差異）。',
    descFallbackHtml:
      '<strong>留空時：</strong>搜尋引擎可能自動摘錄頁面內文（Blocks），摘要不一定如您預期，重要活動頁建議手動填寫。',
    descExample:
      '鎖線選物致力於提供質感生活選品與會員專屬優惠。了解我們的品牌故事與服務承諾。',
    keywordsPurposeHtml:
      '<strong>用途：</strong>寫入 CMS 頁面主檔的 <code>seo_keywords</code>，可供前台產生 <code>meta keywords</code>、結構化資料或站內搜尋加權（依前台實作而定）。',
    keywordsHow: '建議與本頁主題相關的詞，以英文逗號分隔。',
    keywordsExample: '關於我們, 品牌故事, 客服, 購物說明',
  },
}

const copy = computed(() => COPY[props.variant] || COPY.hall)

const sectionHeading = computed(() => copy.value.sectionHeading)

const introHtml = computed(() => copy.value.introHtml)
</script>

<style scoped>
.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #222;
  margin: 0 0 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;
}

.form-label {
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin-bottom: 5px;
}

.form-control {
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.form-row.form-row-block {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.hint-p {
  font-size: 12px;
  color: #6b7280;
  margin: 4px 0 0;
}

.hint-p--example {
  margin-top: 6px;
}

/* 與商品表單頁 .field-hint 對齊（嵌入 product 時） */
.field-hint {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
}

.field-hint.field-hint-example {
  margin-top: 6px;
}

.form-hint {
  font-size: 12px;
  color: #6b7280;
  margin: 4px 0 0;
}

.seo-example-text {
  color: #374151;
}

/* 嵌入商品表單時與既有 field-hint 視覺對齊 */
.commerce-seo-meta-fields :deep(.form-tip-box) {
  margin-top: 10px;
}
</style>
